/**
 * Cart Inventory Checker
 * Validates all items in the cart against current inventory status
 * Runs on cart page load and cart drawer open
 */

class CartInventoryChecker {
  constructor() {
    this.cartItemsElement = document.querySelector("cart-items");
    this.isDrawer = document.getElementById("cart-drawer") !== null;
    this.inventoryCheckInProgress = false;

    this.init();
  }

  init() {
    // Run initial check when page loads
    this.checkInventory();

    // Listen for cart drawer open events
    document.addEventListener("dispatch:cart-drawer:open", () => {
      this.checkInventory();
    });

    // Add event listener for cart updates
    document.addEventListener("on:line-item:change", () => {
      this.checkInventory();
    });
  }

  /**
   * Checks inventory status of all items in the cart
   */
  async checkInventory() {
    if (this.inventoryCheckInProgress) return;
    this.inventoryCheckInProgress = true;

    try {
      // Get current cart contents
      const response = await fetch("/cart.js");
      const cart = await response.json();

      if (!cart.items || cart.items.length === 0) {
        this.inventoryCheckInProgress = false;
        return;
      }

      // Collect all product handles/IDs
      const unavailableItems = [];
      const itemsToUpdate = {};

      // Check each item individually against the product endpoint
      const itemChecks = cart.items.map(async (item, index) => {
        try {
          // Check if product exists and is available
          const productResponse = await fetch(`/products/${item.handle}.js`);

          if (!productResponse.ok) {
            // Product not found (likely in draft or deleted)
            unavailableItems.push({
              line: index + 1,
              variant_id: item.variant_id,
              title: item.title,
              reason: "product_not_found",
            });
            itemsToUpdate[index + 1] = 0; // Remove
            return;
          }

          const product = await productResponse.json();

          // Find the variant in the product
          const variant = product.variants.find(
            (v) => v.id === item.variant_id
          );

          if (!variant) {
            // Variant not found
            unavailableItems.push({
              line: index + 1,
              variant_id: item.variant_id,
              title: item.title,
              reason: "variant_not_found",
            });
            itemsToUpdate[index + 1] = 0; // Remove
            return;
          }

          // Check if variant is completely out of stock
          if (
            !variant.available ||
            (variant.inventory_management === "shopify" &&
              variant.inventory_policy === "deny" &&
              variant.inventory_quantity <= 0)
          ) {
            unavailableItems.push({
              line: index + 1,
              variant_id: item.variant_id,
              title: item.title,
              reason: "out_of_stock",
            });
            itemsToUpdate[index + 1] = 0; // Remove
          }
          // Check if variant has insufficient stock
          else if (
            variant.inventory_management === "shopify" &&
            variant.inventory_policy === "deny" &&
            variant.inventory_quantity < item.quantity
          ) {
            unavailableItems.push({
              line: index + 1,
              variant_id: item.variant_id,
              title: item.title,
              reason: "insufficient_stock",
              available: variant.inventory_quantity,
              requested: item.quantity,
            });
            itemsToUpdate[index + 1] = variant.inventory_quantity; // Adjust to available quantity
          }
        } catch (error) {
          console.error("Error checking product:", error);
        }
      });

      // Wait for all checks to complete
      await Promise.all(itemChecks);

      // If we found unavailable items, update the cart
      if (unavailableItems.length > 0) {
        this.handleUnavailableItems(unavailableItems, itemsToUpdate);
      }
    } catch (error) {
      console.error("Error checking inventory:", error);
    } finally {
      this.inventoryCheckInProgress = false;
    }
  }

  /**
   * Handles unavailable items in the cart
   * @param {Array} unavailableItems - List of unavailable items
   * @param {Object} itemsToRemove - Map of line numbers to quantities (0)
   */
  async handleUnavailableItems(unavailableItems, itemsToRemove) {
    // Create a notification for the user
    this.showUnavailableItemsNotification(unavailableItems);

    // Update cart to remove unavailable items
    await this.updateCart(itemsToRemove);
  }

  /**
   * Shows a notification about unavailable items
   * @param {Array} unavailableItems - List of unavailable items
   */
  showUnavailableItemsNotification(unavailableItems) {
    const cartErrors = document.getElementById("cart-errors");
    if (!cartErrors) return;

    let hasOutOfStock = false;
    let hasInsufficientStock = false;

    let errorMessage = "<p>The following items need your attention:</p><ul>";

    unavailableItems.forEach((item) => {
      errorMessage += `<li>${item.title} - `;

      switch (item.reason) {
        case "product_not_found":
          errorMessage += "This product is no longer available.";
          hasOutOfStock = true;
          break;
        case "variant_not_found":
          errorMessage += "This variant is no longer available.";
          hasOutOfStock = true;
          break;
        case "out_of_stock":
          errorMessage += "This item is out of stock.";
          hasOutOfStock = true;
          break;
        case "insufficient_stock":
          errorMessage += `Only ${item.available} in stock (you requested ${item.requested}). Quantity has been adjusted.`;
          hasInsufficientStock = true;
          break;
        default:
          errorMessage += "This item is unavailable.";
          hasOutOfStock = true;
      }

      errorMessage += "</li>";
    });

    errorMessage += "</ul>";

    // Add appropriate class based on the types of issues
    if (hasOutOfStock) {
      cartErrors.classList.add("cart-errors--out-of-stock");
      cartErrors.classList.remove("cart-errors--insufficient-stock");
    } else if (hasInsufficientStock) {
      cartErrors.classList.add("cart-errors--insufficient-stock");
      cartErrors.classList.remove("cart-errors--out-of-stock");
    }

    cartErrors.innerHTML = errorMessage;
    cartErrors.hidden = false;

    // Auto-hide after 10 seconds
    setTimeout(() => {
      cartErrors.hidden = true;
    }, 10000);
  }

  /**
   * Updates the cart to remove unavailable items
   * @param {Object} itemsToRemove - Map of line numbers to quantities (0)
   */
  async updateCart(itemsToRemove) {
    // Don't proceed if there's nothing to remove
    if (Object.keys(itemsToRemove).length === 0) return;

    // Set a flag to prevent multiple simultaneous updates
    if (this.isUpdatingCart) return;
    this.isUpdatingCart = true;

    const updates = {};

    // Convert line numbers to variant IDs for the updates object
    Object.keys(itemsToRemove).forEach((line) => {
      updates[line] = 0; // Set quantity to 0 to remove
    });

    try {
      // Update cart using the Shopify AJAX API
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        console.error(
          "Server responded with:",
          response.status,
          response.statusText
        );
        throw new Error("Server error when updating cart");
      }

      const result = await response.json();

      // Wait a moment before triggering refreshes to avoid race conditions
      setTimeout(() => {
        // Trigger a single refresh event
        document.dispatchEvent(
          new CustomEvent("cart:refresh-requested", {
            detail: { source: "inventory-checker" },
          })
        );

        this.isUpdatingCart = false;
      }, 300);
    } catch (error) {
      console.error("Error updating cart:", error);
      this.isUpdatingCart = false;
    }
  }
}

// Initialize the checker when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new CartInventoryChecker();
});
